import React from "react";
import { motion } from "framer-motion";
import {
  HardHat, ArrowRight, FileSearch, Clock, EyeOff,
  Target, BellRing, Radar, CheckCircle2, ArrowLeft,
} from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { useAuth } from "@/context/AuthContext";

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5 },
};

const stats = [
  { value: "$700B+", label: "Public-sector contract spend tracked annually" },
  { value: "Daily", label: "Opportunity feeds refreshed from official sources" },
  { value: "0–1000", label: "Readiness score across 8 qualification factors" },
  { value: "5 min", label: "To build your profile and see your first matches" },
];

const problems = [
  { icon: Clock, p: "You burn days writing bids for contracts you were never qualified to win.", s: "Win probability + qualification scoring tells you which bids are worth your time — before you start." },
  { icon: EyeOff, p: "You can't see who you're up against or what it actually takes to win.", s: "Competitive intelligence surfaces incumbents and top issuing organizations from real award history." },
  { icon: BellRing, p: "Deadlines slip through the cracks across a dozen government portals.", s: "Every pursuit lives in one CRM with automated email reminders before each deadline." },
];

const faqs = [
  { q: "Is the opportunity data real?", a: "Yes. We pull live data directly from official government procurement and grant sources, refreshed daily — nothing is scraped or fabricated." },
  { q: "Is the Free plan really free?", a: "Yes — free forever, no credit card required. You get your Readiness Score, 5 opportunity matches per month, and the profile builder. Upgrade only when you need unlimited opportunities and AI scoring." },
  { q: "How accurate is the win probability?", a: "It's an AI-generated estimate based on your contractor profile and historical award patterns. Treat it as directional guidance to prioritize pursuits — not a guarantee. The more complete your profile, the sharper the estimate." },
  { q: "What trades do you support?", a: "General contracting, electrical, HVAC, plumbing, roofing, concrete, civil, and specialty trades. Your trade maps to the right NAICS codes so opportunity matching is relevant." },
  { q: "Can my whole team use it?", a: "Yes. Pro and Enterprise plans include team seats with roles, so your estimators and BD staff can collaborate in one workspace." },
  { q: "How does billing work and can I cancel?", a: "Plans are billed monthly and renew automatically. You can upgrade, downgrade, change your card, or cancel anytime from the self-serve billing portal — no emails or phone calls." },
];

export default function Platform() {
  const { user, login } = useAuth();
  const go = () => (user ? (window.location.href = "/dashboard") : login());

  return (
    <div className="min-h-screen bg-[#fafdfb] text-slate-900 font-sans antialiased">
      {/* Nav */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2" data-testid="platform-home-link">
            <div className="h-8 w-8 rounded-lg bg-emerald-900 flex items-center justify-center">
              <HardHat className="h-4 w-4 text-lime-300" strokeWidth={2.2} />
            </div>
            <span className="font-heading font-bold text-lg tracking-tight">BidMatchAI</span>
          </a>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
            <a href="/" className="hover:text-slate-900 transition-colors">Home</a>
            <a href="/#features" className="hover:text-slate-900 transition-colors">Features</a>
            <a href="/#pricing" className="hover:text-slate-900 transition-colors">Pricing</a>
          </nav>
          <button onClick={go} data-testid="platform-signin-button" className="bg-emerald-900 text-white hover:bg-emerald-800 rounded-md px-5 py-2.5 text-sm font-medium transition-all shadow-sm hover:-translate-y-0.5">
            {user ? "Dashboard" : "Sign in"}
          </button>
        </div>
      </header>

      {/* Page hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 -z-10">
          <div className="absolute -top-24 right-10 h-[420px] w-[420px] rounded-full bg-lime-300/30 blur-[120px]" />
          <div className="absolute top-32 -left-20 h-[380px] w-[380px] rounded-full bg-emerald-400/25 blur-[120px]" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 lg:py-24">
          <motion.div {...fadeUp} className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-6">
            <Radar className="h-3.5 w-3.5 text-lime-500" /> Why BidMatchAI
          </motion.div>
          <motion.h1 {...fadeUp} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[0.98] max-w-3xl">
            The intelligence behind smarter bids.
          </motion.h1>
          <motion.p {...fadeUp} transition={{ duration: 0.5, delay: 0.1 }} className="mt-6 text-lg text-slate-600 max-w-2xl">
            BidMatchAI turns the firehose of public contract data into a clear answer: should you bid, and what will it take to win? Here's what powers it.
          </motion.p>
        </div>
      </section>

      {/* Problem -> solution */}
      <section className="bg-emerald-950 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <motion.h2 {...fadeUp} className="font-heading text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">Stop guessing. Start bidding with confidence.</motion.h2>
          <div className="mt-12 space-y-5">
            {problems.map((row) => (
              <motion.div key={row.p} {...fadeUp} className="grid md:grid-cols-2 gap-5 items-stretch">
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-7">
                  <row.icon className="h-6 w-6 text-red-300" />
                  <p className="mt-3 text-emerald-50/90 font-medium">{row.p}</p>
                </div>
                <div className="rounded-2xl border border-lime-400/20 bg-lime-400/[0.06] p-7">
                  <CheckCircle2 className="h-6 w-6 text-lime-400" />
                  <p className="mt-3 text-lime-50">{row.s}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Stats */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((s) => (
            <motion.div key={s.label} {...fadeUp} className="rounded-2xl border border-slate-200 bg-white p-7">
              <p className="font-data text-4xl font-semibold text-emerald-700 tracking-tight">{s.value}</p>
              <p className="mt-2 text-sm text-slate-600">{s.label}</p>
            </motion.div>
          ))}
        </div>
        <p className="mt-4 text-xs text-slate-400">Spend figures reflect publicly reported U.S. federal contract obligations via USAspending.gov.</p>
      </section>

      {/* 4. FAQ */}
      <section className="bg-emerald-50/50 border-y border-emerald-100">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-24">
          <motion.h2 {...fadeUp} className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-center">Questions, answered.</motion.h2>
          <motion.div {...fadeUp} className="mt-10">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`} data-testid={`faq-${i}`} className="border-emerald-100">
                  <AccordionTrigger className="text-base font-semibold text-slate-900 hover:no-underline py-5">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-[15px] leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* CTA / footer */}
      <footer className="bg-emerald-950 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
          <motion.h2 {...fadeUp} className="font-heading text-3xl sm:text-4xl font-bold tracking-tight max-w-xl">See your readiness score in five minutes.</motion.h2>
          <button onClick={go} data-testid="platform-cta-button" className="mt-8 bg-lime-400 hover:bg-lime-300 text-emerald-950 rounded-md px-7 py-3.5 font-medium transition-all hover:-translate-y-0.5 inline-flex items-center gap-2">
            Start your 5-day free trial <ArrowRight className="h-4 w-4" />
          </button>
          <p className="mt-4 text-sm text-emerald-300/70">5-day free trial on Growth · Card required · Cancel anytime.</p>
          <div className="mt-10 border-t border-white/10 pt-8 flex items-center justify-between">
            <a href="/" className="text-sm text-emerald-200 hover:text-white transition-colors inline-flex items-center gap-2"><ArrowLeft className="h-4 w-4" /> Back to home</a>
            <span className="text-sm text-emerald-300/70">© 2026 BidMatchAI</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
